import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import useWindowWidth from '../hooks/useWindowWidth';
import './DailySummaryPage.css';
import { toLocalDateInput } from '@sf/shared';

const fmt = (val) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val ?? 0);

const fmtTime = (iso) => {
    if (!iso) return '--:--';
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (iso) => {
    if (!iso) return '';
    return new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR');
};

const today = () => toLocalDateInput(new Date());

const shiftDate = (dateStr, days) => {
    const d = new Date(dateStr + 'T12:00:00');
    d.setDate(d.getDate() + days);
    return toLocalDateInput(d);
};

const statusClass = (s) => {
    if (!s) return '';
    const m = { 'Paga': 'paga', 'Recebida': 'recebida', 'Pendente': 'pendente', 'Atrasada': 'atrasada' };
    return m[s] ?? '';
};

// ── Desktop ──────────────────────────────────────────────────────────────────
function DesktopView({ data, date, onDateChange }) {
    const net = (data?.total_revenue ?? 0) - (data?.total_expense ?? 0);
    const entries = data?.entries ?? [];

    return (
        <div className="ds-page">
            <div className="ds-header">
                <h1 className="ds-title">Lançamentos do Dia</h1>
                <div className="ds-date-nav">
                    <button className="ds-nav-btn" onClick={() => onDateChange(shiftDate(date, -1))}>‹</button>
                    <input
                        type="date"
                        value={date}
                        max={today()}
                        onChange={e => onDateChange(e.target.value)}
                    />
                    <button
                        className="ds-nav-btn"
                        onClick={() => onDateChange(shiftDate(date, 1))}
                        disabled={date >= today()}
                    >›</button>
                </div>
            </div>

            <div className="ds-summary">
                <div className="ds-summary-card revenue">
                    <span className="ds-summary-label">Receitas</span>
                    <span className="ds-summary-value">{fmt(data?.total_revenue)}</span>
                    <span className="ds-summary-count">
                        {entries.filter(e => e.type === 'revenue').length} lançamento(s)
                    </span>
                </div>
                <div className="ds-summary-card expense">
                    <span className="ds-summary-label">Despesas</span>
                    <span className="ds-summary-value">{fmt(data?.total_expense)}</span>
                    <span className="ds-summary-count">
                        {entries.filter(e => e.type === 'expense').length} lançamento(s)
                    </span>
                </div>
                <div className="ds-summary-card result">
                    <span className="ds-summary-label">Resultado</span>
                    <span className="ds-summary-value" style={{ color: net >= 0 ? '#16a34a' : '#dc2626' }}>
                        {fmt(net)}
                    </span>
                    <span className="ds-summary-count">{entries.length} total</span>
                </div>
            </div>

            {entries.length === 0 ? (
                <div className="ds-empty">
                    <div className="ds-empty-icon">📭</div>
                    <div className="ds-empty-text">Nenhum lançamento encontrado para {fmtDate(date)}.</div>
                </div>
            ) : (
                <div className="ds-table-wrap">
                    <table className="ds-table">
                        <thead>
                            <tr>
                                <th>Hora</th>
                                <th>Tipo</th>
                                <th>Descrição</th>
                                <th>Categoria</th>
                                <th>Vencimento</th>
                                <th>Forma</th>
                                <th>Conta / Cartão</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry, i) => {
                                const isRev = entry.type === 'revenue';
                                const cls = entry.is_simulation ? 'simulation' : (isRev ? 'revenue' : 'expense');
                                return (
                                    <tr key={i}>
                                        <td><span className="ds-time">{fmtTime(entry.created_at)}</span></td>
                                        <td>
                                            <span className={`ds-badge ${cls}`}>
                                                <span className={`ds-dot ${cls}`} />
                                                {entry.is_simulation ? 'Simulação' : (isRev ? 'Receita' : 'Despesa')}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="ds-desc">{entry.description}</div>
                                        </td>
                                        <td>
                                            <span className="ds-account">{entry.category ?? '—'}</span>
                                        </td>
                                        <td>
                                            <span className="ds-account">{fmtDate(entry.due_date)}</span>
                                        </td>
                                        <td>
                                            <span className="ds-method">{entry.payment_method ?? '—'}</span>
                                        </td>
                                        <td>
                                            <span className="ds-account">{entry.account ?? '—'}</span>
                                        </td>
                                        <td>
                                            <span className={`ds-status ${statusClass(entry.status)}`}>
                                                {entry.status}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <span className={`ds-amount ${isRev ? 'revenue' : 'expense'}`}>
                                                {isRev ? '+' : '-'}{fmt(entry.amount)}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ── Mobile ────────────────────────────────────────────────────────────────────
function MobileView({ data, date, onDateChange }) {
    const net = (data?.total_revenue ?? 0) - (data?.total_expense ?? 0);
    const entries = data?.entries ?? [];

    return (
        <div className="ds-page-m">
            <div className="ds-header-m">
                <p className="ds-title-m">Lançamentos do Dia</p>
                <div className="ds-date-nav-m">
                    <button className="ds-nav-btn-m" onClick={() => onDateChange(shiftDate(date, -1))}>‹</button>
                    <input
                        type="date"
                        value={date}
                        max={today()}
                        onChange={e => onDateChange(e.target.value)}
                    />
                    <button
                        className="ds-nav-btn-m"
                        onClick={() => onDateChange(shiftDate(date, 1))}
                        disabled={date >= today()}
                    >›</button>
                </div>
            </div>

            <div className="ds-summary-m">
                <div className="ds-summary-card-m revenue">
                    <div className="ds-summary-label-m">Receitas</div>
                    <div className="ds-summary-value-m">{fmt(data?.total_revenue)}</div>
                </div>
                <div className="ds-summary-card-m expense">
                    <div className="ds-summary-label-m">Despesas</div>
                    <div className="ds-summary-value-m">{fmt(data?.total_expense)}</div>
                </div>
                <div className="ds-summary-card-m result">
                    <div className="ds-summary-label-m">Resultado</div>
                    <div className="ds-summary-value-m" style={{ color: net >= 0 ? '#16a34a' : '#dc2626' }}>
                        {fmt(net)}
                    </div>
                </div>
            </div>

            {entries.length === 0 ? (
                <div className="ds-empty-m">
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📭</div>
                    <div>Nenhum lançamento em {fmtDate(date)}</div>
                </div>
            ) : (
                <div className="ds-timeline-m">
                    {entries.map((entry, i) => {
                        const isRev = entry.type === 'revenue';
                        const cls = entry.is_simulation ? 'simulation' : (isRev ? 'revenue' : 'expense');
                        return (
                            <div className="ds-card-m" key={i}>
                                <div className="ds-card-m-left">
                                    <div className="ds-card-m-row">
                                        <span className={`ds-dot ${cls}`} />
                                        <span className="ds-card-m-desc">{entry.description}</span>
                                    </div>
                                    <div className="ds-card-m-row">
                                        {entry.category && (
                                            <span className="ds-card-m-cat">{entry.category}</span>
                                        )}
                                        {entry.category && (entry.account || entry.payment_method) && (
                                            <span className="ds-card-m-cat">·</span>
                                        )}
                                        {entry.account && (
                                            <span className="ds-card-m-meta">{entry.account}</span>
                                        )}
                                    </div>
                                    <div className="ds-card-m-row">
                                        {entry.payment_method && (
                                            <span className="ds-method">{entry.payment_method}</span>
                                        )}
                                        <span className={`ds-status ${statusClass(entry.status)}`}>
                                            {entry.status}
                                        </span>
                                    </div>
                                </div>
                                <div className="ds-card-m-right">
                                    <span className={`ds-card-m-amount ${isRev ? 'revenue' : 'expense'}`}>
                                        {isRev ? '+' : '-'}{fmt(entry.amount)}
                                    </span>
                                    <span className="ds-card-m-time">{fmtTime(entry.created_at)}</span>
                                    <span className="ds-card-m-time">venc. {fmtDate(entry.due_date)}</span>
                                    {entry.is_simulation && (
                                        <span className="ds-badge simulation" style={{ fontSize: '0.65rem' }}>
                                            Simulação
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function DailySummaryPage() {
    const [date, setDate] = useState(today());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const width = useWindowWidth();
    const isMobile = width < 768;

    const fetchData = useCallback(() => {
        setLoading(true);
        api.get(`/daily-summary?date=${date}`)
            .then(r => setData(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [date]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) {
        return (
            <div className="text-center mt-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </div>
            </div>
        );
    }

    return isMobile
        ? <MobileView data={data} date={date} onDateChange={setDate} />
        : <DesktopView data={data} date={date} onDateChange={setDate} />;
}
